import React, {useState} from 'react';
import cn from 'classnames';

import Skeleton from 'coreComponents/Skeleton';
import {useUI} from 'coreHooks';
import useChangeEffect from 'coreHooks/useChangeEffect';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';

import {BadgeRaw, IProductBadgesProps} from './IProductBadges';

import styles from './ProductBadges.module.scss';

const ProductBadges = ({
	badgesList,
	className,
	excludeFillVariants = false,
	showOnlyFillVariants = false,
}: IProductBadgesProps) => {
	const pdpZoomConfig = useUI('pdpZoom');
	const isZoomActive = pdpZoomConfig.isActive;
	const [badges, setBadges] = useState<BadgeRaw[]>([]);
	const [loaded, setLoaded] = useState(false);
	const {managerRepository} = useGeneralProvider();

	const sortBadges = (arr) => (arr.sort((a, b) => {
		if (a.variant === 'fill' && b.variant !== 'fill') {
			return 1;
		} else if (a.variant !== 'fill' && b.variant === 'fill') {
			return -1;
		} else {
			return 0;
		}
	}));

	// Not again when a page kept alive (`<Activity>`) is shown again: the badges are there.
	useChangeEffect(() => {
		const cmsProvider = managerRepository.getContentMgr().getCMSProvider();

		cmsProvider
			.getContent('badgeItems', {
				variables: {
					ids: badgesList,
				},
			})
			.then((data) => {
				const unsortedBadge = data?.badgeCollection?.items || [];
				const badgesRaw = unsortedBadge.length > 1 ? sortBadges(unsortedBadge) : unsortedBadge;

				if (!excludeFillVariants && !showOnlyFillVariants) {
					return setBadges(badgesRaw);
				}

				const isNotFillVariant = (badge: BadgeRaw) => badge.variant !== 'fill';
				const isFillVariant = (badge: BadgeRaw) => badge.variant === 'fill';

				const filterBadges = (badges: BadgeRaw[], condition: (badge: BadgeRaw) => boolean): BadgeRaw[] => {
					return badges.filter(condition);
				};

				if (excludeFillVariants) {
					setBadges(filterBadges(badgesRaw, isNotFillVariant));
				} else if (showOnlyFillVariants) {
					setBadges(filterBadges(badgesRaw, isFillVariant));
				}
			})
			.finally(() => setLoaded(true));
	}, [badgesList], true);

	if (isZoomActive) {
		return null;
	}

	if (!loaded) {
		return <Skeleton height="24px" />;
	}

	if (!badges.length) {
		return null;
	}

	return (
		<div className={cn(styles['product-badges'], className)}>
			{badges.map((badge, i) => {
				switch (badge.variant) {
					case 'fill':
						return (
							<span
								className={styles[badge.variant]}
								style={{backgroundColor: badge.color || '#fff'}}
								key={badge.label + i}
							>
								{badge.label}
							</span>
						);
					default:
						return (
							<span style={{color: badge.color || '#000'}} key={badge.label + i}>
								{badge.label}
							</span>
						);
				}
			})}
		</div>
	);
};

export default ProductBadges;
